/**
 * Advisor Transactions Report.
 * @module advisor/components/reports/AdvisorTransactionsReport
 *
 */

import React, {useEffect, useState} from 'react';
import debug from 'debug';
import {connect} from 'react-redux';
import axiosInstance from '../../../shared/utils/axios';
import {_openModal} from '../../../shared/reducers/modals';
import moment from 'moment';
import lazy from 'lazy.js';
import {_getUnreadReports, isEntitled} from '../../../shared/store';
import PropTypes from 'prop-types';
import {Checkbox, DatePicker, DescriptiveText, Dropdown, LoadingSpinner, Row, SectionTitle, Steps} from '../../../shared';

const log = debug('circleblack:report:advisorTransactionsReport');

/**
 * Advisor Transactions Report component.
 *
 * @name AdvisorTransactionsReport
 * @requires module:shared/components/Button
 * @requires module:shared/components/SectionTitle
 * @requires module:shared/components/Dropdown
 * @requires module:shared/components/Checkbox
 *
 */
const AdvisorTransactionsReport = props => {
  const [advisor, setSelectedAdvisor] = useState({});
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const today = moment().format('YYYY-MM-DD');

  const setupParams = report => {
    log('setupParams');
    const parameters = lazy(report.parameters).reject({name: 'format'}).indexBy('name').toObject();

    for (let p in parameters) {
      if (parameters.hasOwnProperty(p)) {
        const param = parameters[p];
        if (['cash', 'heldAway', 'thirdParty'].includes(param.name)) {
          param.value = param.default || false;
        } else {
          param.value = param.default || '';
        }
        if (param.name === 'firmID') {
          param.value = props.session.profile.firm.id;
        }
      }
    }
    return parameters;
  };

  const [params, setParams] = useState(setupParams(props.report));

  const advisorTypeAhead = (inputValue) => axiosInstance.get(
    '/crm/advisors',
    {params: {displayName: `%${inputValue}`, sort: '+displayName', limit: 50}}
  )
    .then(res => res.data.map((adv) => ({value: adv.id, label: adv.displayName})))
    .catch(err => setErrorMessage('Failed to Retrieve Advisors: ' + err));

  useEffect(() => {
    setLoading(false);
  }, []);

  const canGenerate = (parameters) => {
    const validTransactionType = ['heldAway', 'thirdParty', 'cash'].filter((key) => parameters[key].value);
    const invalidParamType = ['firmID', 'householdID', 'startDate', 'endDate'].filter((key) => !parameters[key].value);
    return !!validTransactionType.length && !invalidParamType.length;
  };

  const generateReport = () => {
    if (!isEntitled([{group: 'compliance', feature: 'core', access: 'r'}])) {
      return;
    }
    // ETODO Date Validation should be centralized, perhaps in Steps ticket
    if (moment(params.endDate.value, 'YYYY-MM-DD').isBefore(params.startDate.value, 'YYYY-MM-DD')) {
      return props.openModal({
        type: 'MessageModal',
        title: 'Invalid Start and End Date.',
        body: 'Cannot generate report for an end date before the start date.',
      });
    }
    return axiosInstance.post(
      '/reports/' + props.report.name,
      {parameters: Object.values(params).filter((param) => param.value).map((param) => ({id: param.id, value: param.value}))}
    )
      .then(() => {
        setSuccessMessage('The report you requested is generating. You may view it shortly.');
        setStep('END');

        props.getUnreadReports(props.unreadReportsTimeoutID, props.unreadReports, props.reportPageVisitTimestamp, 30000);
      })
      .catch(err => {
        const title = err.response && err.response.data && err.response.data.error && err.response.data.error.title !== undefined
          ? err.response.data.error.title
          : 'Report Couldn\'t be created';
        const body = (err.response && err.response.data && err.response.data.error && err.response.data.error.msg) || err.message;
        setErrorMessage(title + ': ' + body);
      });
  };

  const updateValue = (name, option) => {
    let selected = option || {id: null, value: null};
    log('newState', selected);
    setSelectedAdvisor(selected);
    setParams({...params, householdID: {...params.householdID, value: selected.value}});
  };

  const renderContent = () => <div>
    <SectionTitle title='Advisor'/>
    <Dropdown title='Advisor'
              placeholder='select advisor'
              type='typeahead'
              dataPoint={{
                name: 'advisor',
                value: advisor.value && {label: advisor.label, value: advisor.value},
              }}
              onChange={updateValue}
              dataFunction={advisorTypeAhead}
              isClearable
              width='50%'
              required/>
    <SectionTitle title={'Dates'}/>
    <Row>
      <DatePicker title={'Start Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'startDate', value: params.startDate.value}}
                  onChange={(name, value) => {
                    setParams({...params, startDate: {...params.startDate, value: value}});
                  }}
                  maxDate={today}/>
      <DatePicker title={'End Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'endDate', value: params.endDate.value}}
                  onChange={(name, value) => {
                    setParams({...params, endDate: {...params.endDate, value: value}});
                  }}
                  maxDate={today}/>
    </Row>
    <SectionTitle title={'Transaction Types'}/>
    <Checkbox
      title={'Held Away Transactions'}
      dataPoint={{name: params.heldAway.name, value: params.heldAway.value || false}}
      onChange={() => {
        setParams({...params, heldAway: {...params.heldAway, value: !params.heldAway.value}});
      }}
    />
    <Checkbox
      title={'Third Party Transactions'}
      dataPoint={{name: params.thirdParty.name, value: params.thirdParty.value || false}}
      onChange={() => {
        setParams({...params, thirdParty: {...params.thirdParty, value: !params.thirdParty.value}});
      }}
    />
    <Checkbox
      title={'Cash Transactions'}
      dataPoint={{name: params.cash.name, value: params.cash.value || false}}
      onChange={() => {
        setParams({...params, cash: {...params.cash, value: !params.cash.value}});
      }}
    />
  </div>;

  if (loading) {
    return <LoadingSpinner/>;
  }

  return <Steps
    height={'100%'}
    completedStateContent={<DescriptiveText>{successMessage || ''}</DescriptiveText>}
    steps={[
      {
        title: 'Generate Advisor Transactions - Select Parameters',
        content: renderContent,
        next: {
          label: 'Generate',
          action: generateReport,
          disabled: !canGenerate(params),
        },
      },
    ].filter(s => !!s)}
    stepIndex={step}
    cancelAction={props.cancelAction}
    canStepBack={false}
    errorMessage={errorMessage}/>;
};

const mapStateToProps = state => ({
  session: state.session,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  unreadReports: state.badges.unreadReports,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
});

AdvisorTransactionsReport.propTypes = {
  /**
   * The Report to be generated
   */
  report: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  /**
   * Is from Models Reducer
   */
  openModal: PropTypes.func.isRequired,
  /**
   * To reset the Report generate tile to default state
   */
  cancelAction: PropTypes.func.isRequired,
  /**
   /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      firm: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }),
    }),
  }).isRequired,
  /**
   * Are from badges reducer
   */
  getUnreadReports: PropTypes.func,
  unreadReportsTimeoutID: PropTypes.object,
  unreadReports: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorTransactionsReport);
